import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { applyTheme, abs } from "../lib/theme";
import { compressImage } from "../lib/imageOptimizer";
import { Button } from "../components/ui/button";
import {
  GraduationCap, UploadCloud, FileText, CheckCircle2, AlertTriangle, Loader2, RefreshCw, X,
} from "lucide-react";

const ACCEPT = "image/jpeg,image/png,application/pdf";
const MAX_MB_FALLBACK = 10;

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function CandidateReupload() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    if (!token) { setLoading(false); setInfo({ valid: false, detail: "This upload link is missing its access token." }); return; }
    (async () => {
      try {
        const { data } = await api.get("/public/reupload-info", { params: { token } });
        if (!alive) return;
        setInfo(data);
        if (data?.valid) {
          applyTheme({
            branding: data.branding || {},
            title: `Re-upload ${data.document_title || "document"} — ${data.institution_name || "EduPulse"}`,
          });
        }
      } catch (e) {
        if (alive) setInfo({ valid: false, detail: formatApiError(e.response?.data?.detail) || "This upload link has expired or has already been used." });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    if (!file || file.type === "application/pdf") { setPreview(""); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const maxMb = info?.max_mb || MAX_MB_FALLBACK;

  const takeFile = useCallback((f) => {
    setErr("");
    if (!f) return;
    const okType = ["image/jpeg", "image/jpg", "image/png", "application/pdf"].includes(f.type);
    if (!okType) { setErr("Please choose a PDF, JPG or PNG file."); return; }
    if (f.size > maxMb * 1024 * 1024) { setErr(`That file is larger than ${maxMb} MB. Try a photo instead of a scan, or a lower-resolution export.`); return; }
    setFile(f);
  }, [maxMb]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    takeFile(e.dataTransfer?.files?.[0]);
  };

  const submit = async () => {
    if (!file || submitting) return;
    setSubmitting(true);
    setErr("");
    try {
      let toSend = file;
      if (file.type.startsWith("image/")) {
        try { toSend = await compressImage(file, { maxSizeMB: Math.min(4, maxMb) }); } catch { toSend = file; }
      }
      const fd = new FormData();
      fd.append("token", token);
      fd.append("file", toSend, toSend.name || file.name || "document");
      await api.post("/public/reupload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
    } catch (e) {
      const status = e.response?.status;
      if (status === 410) {
        setInfo({ valid: false, detail: "This upload link has expired or has already been used." });
      } else {
        setErr(formatApiError(e.response?.data?.detail) || "Upload failed. Please check your connection and try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const primary = info?.branding?.primary_color || "#1E40AF";
  const logo = abs(info?.branding?.logo_url);

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center px-4 py-8 sm:py-14">
      <div className="w-full max-w-md">
        <Header logo={logo} name={info?.institution_name} primary={primary} />

        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/70 border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-10 flex flex-col items-center gap-3 text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin" />
              <p className="text-sm">Loading your document request…</p>
            </div>
          ) : !info?.valid ? (
            <ExpiredState detail={info?.detail} />
          ) : done ? (
            <SuccessState />
          ) : (
            <div className="p-5 sm:p-6 space-y-5">
              <DiscrepancyCallout
                docTitle={info.document_title}
                reason={info.rejection_reason}
                candidate={info.candidate_name}
              />

              {!file ? (
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={onDrop}
                  className={`w-full rounded-xl border-2 border-dashed px-4 py-9 flex flex-col items-center gap-2 text-center transition-colors ${
                    dragging ? "border-slate-500 bg-slate-50" : "border-slate-300 hover:border-slate-400 bg-slate-50/60"
                  }`}
                >
                  <UploadCloud className="w-8 h-8 text-slate-400" />
                  <span className="font-medium text-slate-700 text-sm">Tap to take a photo or choose a file</span>
                  <span className="text-xs text-slate-400">PDF, JPG or PNG · up to {maxMb} MB</span>
                </button>
              ) : (
                <FilePreview file={file} preview={preview} onClear={() => { setFile(null); setErr(""); }} />
              )}

              <input
                ref={inputRef}
                type="file"
                accept={ACCEPT}
                capture="environment"
                className="hidden"
                onChange={(e) => takeFile(e.target.files?.[0])}
              />

              {err && (
                <p className="text-sm text-red-600 flex items-start gap-1.5">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{err}</span>
                </p>
              )}

              <Button
                onClick={submit}
                disabled={!file || submitting}
                className="w-full h-11 text-[15px] font-semibold text-white"
                style={{ backgroundColor: primary }}
              >
                {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</>) : "Submit Replacement Document"}
              </Button>

              <p className="text-[11px] leading-relaxed text-slate-400 text-center">
                This secure link is time-limited and can only be used once. Do not share it with anyone.
              </p>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">Powered by EduPulse Admissions</p>
      </div>
    </div>
  );
}

function Header({ logo, name, primary }) {
  return (
    <div className="flex items-center gap-3 mb-5 px-1">
      {logo ? (
        <img src={logo} alt={name || "Institution"} className="h-10 w-10 rounded-lg object-contain bg-white border border-slate-200 p-1"
          onError={(e) => { e.currentTarget.style.display = "none"; }} />
      ) : (
        <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: primary }}>
          <GraduationCap className="w-5 h-5" />
        </div>
      )}
      <div className="min-w-0">
        <div className="font-semibold text-slate-800 truncate">{name || "Admissions Office"}</div>
        <div className="text-xs text-slate-500">Document verification</div>
      </div>
    </div>
  );
}

function DiscrepancyCallout({ docTitle, reason, candidate }) {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
        <AlertTriangle className="w-4 h-4" /> Action required
      </div>
      <p className="text-sm text-amber-900 mt-1.5">
        {candidate ? `${candidate}, your ` : "Your "}
        <span className="font-semibold">{docTitle || "document"}</span> could not be verified and needs to be re-submitted.
      </p>
      {reason && (
        <div className="mt-3 rounded-lg bg-white border border-amber-200 px-3 py-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">Reviewer's note</div>
          <p className="text-sm text-slate-700 mt-0.5">{reason}</p>
        </div>
      )}
    </div>
  );
}

function FilePreview({ file, preview, onClear }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-3">
      {preview ? (
        <img src={preview} alt="Preview" className="h-14 w-14 rounded-lg object-cover border border-slate-200" />
      ) : (
        <div className="h-14 w-14 rounded-lg bg-slate-100 flex items-center justify-center">
          <FileText className="w-6 h-6 text-slate-400" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-slate-800 truncate">{file.name || "document"}</div>
        <div className="text-xs text-slate-400">{fmtSize(file.size)}</div>
      </div>
      <button type="button" onClick={onClear} aria-label="Remove file"
        className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function SuccessState() {
  return (
    <div className="p-8 flex flex-col items-center text-center gap-3">
      <div className="h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center">
        <CheckCircle2 className="w-8 h-8 text-emerald-600" />
      </div>
      <h2 className="font-semibold text-lg text-slate-800">Document uploaded successfully!</h2>
      <p className="text-sm text-slate-500">
        Our admissions team will review it shortly. You can close this page now — there is nothing more to do.
      </p>
    </div>
  );
}

function ExpiredState({ detail }) {
  return (
    <div className="p-8 flex flex-col items-center text-center gap-3">
      <div className="h-14 w-14 rounded-full bg-red-100 flex items-center justify-center">
        <RefreshCw className="w-7 h-7 text-red-500" />
      </div>
      <h2 className="font-semibold text-lg text-slate-800">This link is no longer active</h2>
      <p className="text-sm text-slate-500">
        {detail || "This upload link has expired or has already been used."}
      </p>
      <p className="text-sm text-slate-500">
        Please contact your admissions counselor and ask them to send a fresh upload link.
      </p>
    </div>
  );
}
